import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { csAdminApi } from '../../services/api';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  TextField,
  Box,
  Typography,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  LinearProgress,
  Paper,
  Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudUploadOutlined as UploadIcon,
  DescriptionOutlined as FileIcon,
  CheckCircleOutlined as SuccessIcon,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { useLookups } from '../../hooks/useLookups';
import {
  findLookupItem,
  getDocumentTypeRules,
  validateDocumentDates,
} from '../../utils/documentTypeFields';

const DocumentUploadModal = ({
  open,
  onClose,
  onUpload,
  entityType = 'director',
  entityId,
  companyId,
  presetDocumentType = null,
}) => {
  const { items: documentTypes } = useLookups('DIRECTOR_DOCUMENT_TYPE');
  const typeLocked = Boolean(presetDocumentType?.code || presetDocumentType?.label);
  const [documentType, setDocumentType] = useState('');
  const [documentNumber, setDocumentNumber] = useState('');
  const [issueDate, setIssueDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const selectedType = useMemo(
    () => findLookupItem(documentTypes, documentType),
    [documentTypes, documentType]
  );
  const fieldRules = useMemo(() => getDocumentTypeRules(selectedType), [selectedType]);

  useEffect(() => {
    if (!open) return;
    const presetCode = presetDocumentType?.code || '';
    setDocumentType(presetCode);
    setDocumentNumber('');
    setIssueDate('');
    setExpiryDate('');
    setNotes('');
    setFile(null);
    setError(null);
    setSuccess(false);
    setUploadProgress(0);
  }, [open, presetDocumentType]);

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles && acceptedFiles.length > 0) {
      const uploadedFile = acceptedFiles[0];

      if (uploadedFile.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }

      const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
      if (!validTypes.includes(uploadedFile.type)) {
        setError('Only PDF, JPG, and PNG files are allowed');
        return;
      }

      setFile(uploadedFile);
      setError(null);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
    },
    multiple: false,
    maxSize: 10 * 1024 * 1024,
  });

  const validateForm = () => {
    if (!documentType) {
      setError('Please select a document type');
      return false;
    }
    if (fieldRules.requiresDocumentNumber && !documentNumber.trim()) {
      setError('Please enter the document number');
      return false;
    }
    const dateError = validateDocumentDates({ issueDate, expiryDate, rules: fieldRules });
    if (dateError) {
      setError(dateError);
      return false;
    }
    if (!file) {
      setError('Please upload a file');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setUploading(true);
    setError(null);

    try {
      for (let i = 0; i <= 100; i += 10) {
        setUploadProgress(i);
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('owner_type', String(entityType || 'director').toUpperCase());
      formData.append('owner_id', entityId ?? '');
      if (companyId) {
        formData.append('company_id', companyId);
      }
      formData.append('doc_category', documentType || '');
      formData.append('document_name', selectedType?.label || file?.name || documentType || 'document');
      formData.append('documentNumber', documentNumber || '');
      formData.append('issueDate', issueDate || '');
      formData.append('expiryDate', expiryDate || '');
      formData.append('notes', notes || '');

      await csAdminApi.uploadDocument(formData);

      setSuccess(true);
      setTimeout(() => {
        if (onUpload) {
          onUpload({
            documentType,
            documentNumber,
            issueDate,
            expiryDate,
            notes,
            fileName: file.name,
            fileSize: file.size,
          });
        }
        handleClose();
      }, 1000);
    } catch (err) {
      setError('Failed to upload document. Please try again.');
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setDocumentType('');
    setDocumentNumber('');
    setIssueDate('');
    setExpiryDate('');
    setNotes('');
    setFile(null);
    setError(null);
    setSuccess(false);
    setUploadProgress(0);
    onClose();
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${Math.round((bytes / k ** i) * 100) / 100} ${sizes[i]}`;
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" component="div">
            Upload Document
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{ color: 'grey.500' }}
            disabled={uploading}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} icon={<SuccessIcon />}>
            Document uploaded successfully!
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {typeLocked ? (
            <Box>
              <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 0.5 }}>
                Document type
              </Typography>
              <Chip
                label={presetDocumentType?.label || presetDocumentType?.code}
                color="primary"
                variant="outlined"
              />
            </Box>
          ) : (
            <FormControl fullWidth required>
              <InputLabel>Document Type</InputLabel>
              <Select
                value={documentType}
                label="Document Type"
                onChange={(e) => setDocumentType(e.target.value)}
                disabled={uploading}
              >
                {documentTypes.map((type) => (
                  <MenuItem key={type.code} value={type.code}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField
            fullWidth
            required={fieldRules.requiresDocumentNumber}
            label={fieldRules.requiresDocumentNumber ? 'Document Number' : 'Document Number (optional)'}
            value={documentNumber}
            onChange={(e) => setDocumentNumber(e.target.value)}
            placeholder="Enter document number"
            disabled={uploading}
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              required={fieldRules.requiresIssueDate}
              label={fieldRules.requiresIssueDate ? 'Issue Date' : 'Issue Date (optional)'}
              type="date"
              value={issueDate}
              onChange={(e) => setIssueDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={uploading}
            />
            <TextField
              fullWidth
              required={fieldRules.requiresExpiry}
              label={fieldRules.requiresExpiry ? 'Expiry Date' : 'Expiry Date (optional)'}
              type="date"
              value={expiryDate}
              onChange={(e) => setExpiryDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={uploading}
              helperText={fieldRules.requiresExpiry ? 'Required for this document type' : undefined}
            />
          </Box>

          <Box>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Upload File <span style={{ color: '#d32f2f' }}>*</span>
            </Typography>
            <Paper
              {...getRootProps()}
              sx={{
                border: 2,
                borderColor: isDragActive ? 'primary.main' : 'divider',
                borderStyle: 'dashed',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                cursor: uploading ? 'not-allowed' : 'pointer',
                bgcolor: isDragActive ? 'action.hover' : 'background.paper',
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: uploading ? 'divider' : 'primary.main',
                  bgcolor: uploading ? 'background.paper' : 'action.hover',
                },
              }}
            >
              <input {...getInputProps()} disabled={uploading} />
              {file ? (
                <Box>
                  <FileIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {file.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatFileSize(file.size)}
                  </Typography>
                  {!uploading && (
                    <Button
                      size="small"
                      sx={{ mt: 1 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setFile(null);
                      }}
                    >
                      Change File
                    </Button>
                  )}
                </Box>
              ) : (
                <Box>
                  <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    {isDragActive ? 'Drop the file here' : 'Drag & Drop file here'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    or
                  </Typography>
                  <Button variant="outlined" size="small">
                    Browse Files
                  </Button>
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 2 }}>
                    Max size: 10MB • Formats: PDF, JPG, PNG
                  </Typography>
                </Box>
              )}
            </Paper>
          </Box>

          {uploading && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Uploading...
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {uploadProgress}%
                </Typography>
              </Box>
              <LinearProgress variant="determinate" value={uploadProgress} />
            </Box>
          )}

          <TextField
            fullWidth
            label="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            rows={3}
            placeholder="Add any additional notes or comments"
            disabled={uploading}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} color="inherit" disabled={uploading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={uploading || !file || !documentType}
        >
          {uploading ? 'Uploading...' : 'Upload Document'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentUploadModal;
